import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import {
  catchError,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { merge, Observable, of as observableOf, Subject } from 'rxjs';
import { RequestService } from 'src/app/services/request.service';
import { CompaniesOverviewControllerRequestService } from './request-service/companies-overview-controller-request.service';
import { FilterEnabledComponent } from 'src/app/helpers/filter-enabled.component';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { FilterStoreService } from 'src/app/services/filter-store.service';
import {
  PAGE_SIZE_OPTIONS,
  PageIndexService,
} from 'src/app/services/page-index-service';
import { ModalService } from '../../../services/modal.service';
import { CompanyOverview } from './model/company-overview';
import { GeneralHelper } from 'src/app/helpers/general-helper';

@Component({
  selector: 'app-companies',
  templateUrl: './companies-overview.component.html',
  styleUrls: ['./companies-overview.component.scss'],
  providers: [FormatPhoneNumber],
})
export class CompaniesOverviewComponent
  extends FilterEnabledComponent
  implements OnInit, OnDestroy
{
  companies: CompanyOverview[];
  totalItems = 0;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  displayedColumns: string[] = [
    'id',
    'discountGroup',
    'regularName',
    'addresses',
    'phoneNumber',
    'email',
    'website',
    'star',
  ];

  reloadData: Subject<void> = new Subject<void>();
  destroyThisComponent: Subject<void> = new Subject<void>();

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  constructor(
    private requestService: RequestService,
    private companiesOverviewControllerRequestService: CompaniesOverviewControllerRequestService,
    private router: Router,
    protected filterStoreService: FilterStoreService,
    protected pageIndexService: PageIndexService,
    private modalService: ModalService
  ) {
    super(
      [
        { key: 'id', active: false, strictEquality: true, numeric: true },
        {
          key: 'regularName',
          active: false,
          strictEquality: false,
          numeric: false,
        },
        {
          key: 'discountGroup',
          active: false,
          strictEquality: true,
          numeric: false,
        },
        {
          key: 'addresses',
          active: false,
          strictEquality: true,
          numeric: false,
        },
        {
          key: 'addresses__province',
          active: false,
          strictEquality: false,
          numeric: false,
        },
        {
          key: 'telephones__number',
          active: false,
          strictEquality: false,
          numeric: false,
        },
        {
          key: 'emails__email',
          active: false,
          strictEquality: false,
          numeric: false,
        },
      ],
      filterStoreService,
      pageIndexService
    );
    this.companies = [];
  }

  ngOnInit() {
    this.paginator.pageIndex = this.initPageIndex;
    this.paginator.pageSize = this.initPageSize;

    this.initSubscriptions();
  }

  private initSubscriptions(): void {
    this.addSubscription(this.sort.sortChange, () => {
      this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
      this.paginator.pageIndex = 0;
    });

    this.addSubscription(
      this.filtersChanged,
      () => {
        this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
        this.paginator.pageIndex = 0;
        this.reloadData.next();
      },
      GeneralHelper.noop,
      300
    );

    this.initGetOverviewSubscription();
  }

  private initGetOverviewSubscription(): void {
    const getOverviewObservable: Observable<CompanyOverview[]> = merge(
      this.sort.sortChange,
      this.paginator.page,
      this.reloadData
    ).pipe(
      startWith([]),
      switchMap(() => {
        this.pageIndexService.setPageIndex(
          this.getLocalStorageKey(),
          this.paginator.pageIndex
        );
        this.pageIndexService.setPageSize(
          this.getLocalStorageKey(),
          this.paginator.pageSize
        );
        return this.companiesOverviewControllerRequestService.companiesOverview(
          this.generateFilterString(),
          this.sort.active,
          this.sort.direction !== 'desc',
          this.paginator.pageIndex,
          this.paginator.pageSize,
          false
        );
      }),
      map((result) => {
        this.totalItems = result.totalItems;
        return result.items;
      }),
      takeUntil(this.destroyThisComponent),
      catchError(() => {
        return observableOf([]);
      })
    );

    this.addSubscription(getOverviewObservable, (data) => {
      this.companies = data;
      if ((data == null || data.length === 0) && this.paginator.pageIndex > 0) {
        this.paginator.pageIndex = 0;
        this.reloadData.next();
      }
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.reloadData != null) {
      this.reloadData.complete();
      this.reloadData = null;
    }
    if (this.destroyThisComponent != null) {
      this.destroyThisComponent.next();
      this.destroyThisComponent.complete();
      this.destroyThisComponent = null;
    }
  }

  createCompany() {
    this.router.navigateByUrl('/bedrijven/create');
  }

  editRow(row: CompanyOverview): void {
    this.router.navigate(['/bedrijven/edit', row.id]);
  }

  deleteCompany(company) {
    this.modalService.confirmDialog(
      'Bedrijf verwijderen',
      'Bent u zeker dat u dit bedrijf wilt verwijderen?',
      (answer) => {
        if (answer) {
          this.requestService
            .delete('/companies/archive/' + company.id)
            .toPromise()
            .then(() => this.reloadData.next());
        }
      }
    );
  }

  protected getLocalStorageKey(): string {
    return 'CompaniesOverviewComponent';
  }
}
